import { useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import Add from '@mui/icons-material/Add';
import { api } from '../api';
import { Empty, Heading, Props } from '../ui';
import CounterpartyPanel, { editCounterparty } from '../CounterpartyPanel';
import DriverPanel, { editDriver } from '../DriverPanel';
import { canUseSku } from '@zilian/contracts';

const NEW_SKU = '__new__';

type Section = 'partners' | 'counterparties' | 'drivers' | 'mappings';
/** Ordered by how often people come for each; drivers are set up often, mappings rarely. */
const SECTIONS: { value: Section; label: string; subtitle: string }[] = [
  {
    value: 'partners',
    label: '联仓企业',
    subtitle: '同样使用联仓的合作企业，可互相下单、双方确认。',
  },
  {
    value: 'counterparties',
    label: '未入驻单位',
    subtitle: '未使用联仓的客户和供应商，由本企业登记单据、对账和收付款。',
  },
  {
    value: 'drivers',
    label: '提货司机',
    subtitle: '司机首次扫提货码时自动建档，常来的师傅也可以提前录入。',
  },
  {
    value: 'mappings',
    label: '商品对应',
    subtitle: '联仓供应商的商品到货后，入库为我方哪个商品。',
  },
];

// The last tab used is where the page opens next time, so a frequent task stays one click away.
const LAST_TAB = 'zilian.partners.tab';
const remember = (value: Section) => {
  try {
    localStorage.setItem(LAST_TAB, value);
  } catch {
    // Storage blocked: the page simply opens on the first tab.
  }
};
const sectionOf = (asked: string | null) => {
  let last: string | null = null;
  try {
    last = localStorage.getItem(LAST_TAB);
  } catch {
    last = null;
  }
  return (
    SECTIONS.find((s) => s.value === asked) ?? SECTIONS.find((s) => s.value === last) ?? SECTIONS[0]
  );
};

/**
 * 往来单位, one tab per kind of record instead of one long page: platform partners, the
 * counterparties recorded here, pickup drivers and supplier item mappings. The open tab's main
 * action sits in the heading and the tab is kept in the address.
 */
export function Partners(p: Props) {
  const nameOf = (id: string) =>
    id === p.workspace
      ? p.current.name
      : (p.bootstrap.directory.find((d: any) => d.id === id)?.name ?? id.slice(-6));
  const peerOf = (partner: any) =>
    partner.inviter_id === p.workspace ? partner.invitee_id : partner.inviter_id;
  const active = p.bootstrap.partners.filter((x: any) => x.status === 'ACTIVE');
  // Supplier catalogs as seen by this (buying) enterprise, each item carrying our mapping.
  const [catalogs, setCatalogs] = useState<Record<string, any[]>>({});
  useEffect(() => {
    let live = true;
    Promise.all(
      active.map(async (partner: any) => {
        const peer = peerOf(partner);
        return [peer, await api<any[]>(`/catalog/${peer}`).catch(() => [])] as const;
      }),
    ).then((entries) => live && setCatalogs(Object.fromEntries(entries)));
    return () => {
      live = false;
    };
  }, [p.bootstrap]);

  const ownLabel = (s: any) => `${s.code} · ${s.name}（${s.unit}）`;
  const editMapping = async (peer: string, sellerSkuId?: string) => {
    try {
      const catalog = catalogs[peer] ?? (await api<any[]>(`/catalog/${peer}`));
      if (!catalog.length) throw new Error('该企业暂无可订购商品');
      const first =
        catalog.find((s) => s.id === sellerSkuId) ?? catalog.find((s) => !s.mapping) ?? catalog[0];
      p.open({
        title: `设置采购商品对应 · ${nameOf(peer)}`,
        description:
          '对应到我方同一实物的采购物料，没有合适的可直接新建；这里不表示原料加工成成品。双方单位不同时填写换算，例如 1 吨 = 1000 千克。',
        fields: [
          {
            name: 'sellerSkuId',
            label: '供应商商品',
            options: catalog.map((s) => ({
              value: s.id,
              label: `${s.name}${s.spec ? ` · ${s.spec}` : ''}（${s.unit}）${s.mapping ? ' · 已对应' : ''}`,
            })),
            wide: true,
          },
          {
            name: 'buyerSkuId',
            label: '入库为我方采购物料',
            options: [
              { value: NEW_SKU, label: '＋ 新建同名采购物料（复制名称、规格与单位）' },
              ...(p.bootstrap.skus ?? [])
                .filter((s: any) => canUseSku(s.usage, 'BUY'))
                .map((s: any) => ({ value: s.id, label: ownLabel(s) })),
            ],
            wide: true,
          },
          {
            name: 'factor',
            label: '单位换算：1 供应商单位 = ? 我方单位',
            helper: '双方单位相同时留空',
            wide: true,
          },
        ],
        form: {
          sellerSkuId: first.id,
          buyerSkuId: first.mapping?.buyer_sku_id ?? NEW_SKU,
          factor:
            first.mapping && Number(first.mapping.factor) !== 1
              ? String(Number(first.mapping.factor))
              : '',
        },
        submit: (f: any) =>
          api('/mappings', {
            sellerId: peer,
            sellerSkuId: f.sellerSkuId,
            ...(f.buyerSkuId === NEW_SKU ? { createBuyerSku: {} } : { buyerSkuId: f.buyerSkuId }),
            ...(f.factor ? { factor: String(f.factor).trim() } : {}),
          }),
      });
    } catch (e) {
      p.setError((e as Error).message);
    }
  };
  const supplierRows = Object.entries(catalogs).flatMap(([peer, items]) =>
    // Same-name suggestions are not saved mappings yet, so they are not listed here.
    items.filter((s) => s.mapping && !s.mapping.auto).map((s) => ({ peer, sku: s })),
  );
  const invite = () =>
    p.open({
      title: '邀请合作伙伴',
      description: '目录仅包含企业名称。对方确认后才启用协同，不自动共享历史账本。',
      fields: [
        {
          name: 'workspaceId',
          label: '目标企业',
          options: p.bootstrap.directory.map((d: any) => ({ value: d.id, label: d.name })),
        },
      ],
      form: { workspaceId: p.bootstrap.directory[0]?.id },
      submit: (f: any) => api('/partners', f),
    });

  const [params, setParams] = useSearchParams();
  const section = sectionOf(params.get('tab'));
  const go = (value: Section) => setParams({ tab: value }, { replace: true });
  useEffect(() => remember(section.value), [section.value]);
  const action = {
    partners: (
      <Button variant="contained" startIcon={<Add />} onClick={invite}>
        邀请企业
      </Button>
    ),
    counterparties: (
      <Button variant="contained" startIcon={<Add />} onClick={() => editCounterparty(p)}>
        新建单位
      </Button>
    ),
    drivers: (
      <Button variant="contained" startIcon={<Add />} onClick={() => editDriver(p)}>
        新增司机
      </Button>
    ),
    mappings: undefined,
  }[section.value];

  return (
    <>
      <Heading title="往来单位" subtitle={section.subtitle} action={action} />
      <Box sx={{ borderBottom: '1px solid #e7edef', mb: 2.5, mt: -1 }}>
        <Tabs value={section.value} onChange={(_, v) => go(v)}>
          {SECTIONS.map((s) => (
            <Tab key={s.value} value={s.value} label={s.label} />
          ))}
        </Tabs>
      </Box>
      {section.value === 'partners' && (
        <Paper>
          {p.bootstrap.partners.map((partner: any) => {
            const peer = peerOf(partner);
            return (
              <Box
                key={partner.id}
                sx={{
                  px: 3,
                  py: 2,
                  borderBottom: '1px solid #edf1f2',
                  '&:last-of-type': { borderBottom: 0 },
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 2,
                  alignItems: 'center',
                }}
              >
                <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
                  <Avatar sx={{ bgcolor: '#edf3f1', color: 'primary.main' }}>
                    {partner.partner_name.slice(0, 1)}
                  </Avatar>
                  <Box>
                    <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                      {partner.partner_name}
                    </Typography>
                    <Typography color="text.secondary" sx={{ fontSize: 12, mt: 0.25 }}>
                      {partner.status === 'ACTIVE' ? '双方已确认合作' : '等待对方确认'}
                    </Typography>
                  </Box>
                </Stack>
                {partner.status === 'PENDING' && partner.invitee_id === p.workspace ? (
                  <Button
                    variant="contained"
                    onClick={() => p.safe(() => api(`/partners/${partner.id}/accept`, {}))}
                  >
                    确认合作
                  </Button>
                ) : (
                  partner.status === 'ACTIVE' &&
                  // Nothing to map yet: say so in place instead of a button that only errors.
                  (catalogs[peer]?.length === 0 ? (
                    <Typography color="text.secondary" sx={{ fontSize: 13 }}>
                      对方暂无可售商品
                    </Typography>
                  ) : (
                    <Button variant="outlined" onClick={() => editMapping(peer)}>
                      设置采购商品对应
                    </Button>
                  ))
                )}
              </Box>
            );
          })}
          {!p.bootstrap.partners.length && <Empty>还没有联仓合作企业，点右上角邀请</Empty>}
        </Paper>
      )}
      {section.value === 'counterparties' && <CounterpartyPanel {...p} />}
      {section.value === 'drivers' && <DriverPanel {...p} />}
      {section.value === 'mappings' && (
        <Paper>
          {supplierRows.length > 0 && (
            <Box sx={{ overflowX: 'auto' }}>
              <Table
                sx={{
                  '& th, & td': { px: 1.5 },
                  '& th:first-of-type, & td:first-of-type': { pl: 3 },
                }}
              >
                <TableHead>
                  <TableRow sx={{ '& th': { whiteSpace: 'nowrap' } }}>
                    <TableCell>供应商商品</TableCell>
                    <TableCell>供应商</TableCell>
                    <TableCell>入库为我方商品</TableCell>
                    <TableCell>单位换算</TableCell>
                    <TableCell align="right" sx={{ pr: 3 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {supplierRows.map(({ peer, sku }) => (
                    <TableRow key={sku.id} hover>
                      <TableCell sx={{ fontWeight: 600 }}>{sku.name}</TableCell>
                      <TableCell sx={{ color: 'text.secondary' }}>{nameOf(peer)}</TableCell>
                      <TableCell>
                        {sku.mapping.buyer_name}
                        <Box component="span" sx={{ color: 'text.secondary', ml: 1 }}>
                          {sku.mapping.buyer_code}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary' }}>
                        {Number(sku.mapping.factor) !== 1
                          ? `1 ${sku.unit} = ${Number(sku.mapping.factor)} ${sku.mapping.buyer_unit}`
                          : `同为 ${sku.unit}`}
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap', pr: 3 }}>
                        <Button size="small" onClick={() => editMapping(peer, sku.id)}>
                          修改
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
          {!supplierRows.length && (
            <Empty>还没有设置。在“联仓企业”中点“设置采购商品对应”即可添加。</Empty>
          )}
        </Paper>
      )}
    </>
  );
}
